import { useEffect, useRef, useState } from "react";
import { Download, Mail } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const iso = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => iso(new Date(Date.now() - n * 86400000));

const field = "w-full px-3.5 py-2.5 rounded-xl border border-[#D9DEE4] bg-white text-[14px] focus:outline-none focus:ring-2 focus:ring-[#334E68]";
const lbl = "block text-[12px] font-semibold uppercase tracking-[0.08em] text-[#5B6B7B] mb-1.5";

function parseExportParam() {
  const m = /^(\d{4}-\d{2}-\d{2})_(\d{4}-\d{2}-\d{2})$/.exec(new URLSearchParams(window.location.search).get("export") || "");
  return m ? { start: m[1], end: m[2] } : null;
}

export function ExportPanel({ token }) {
  const preset = useRef(parseExportParam());
  const [start, setStart] = useState(preset.current?.start || daysAgo(29));
  const [end, setEnd] = useState(preset.current?.end || iso(new Date()));
  const [format, setFormat] = useState("xlsx");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const download = async (s = start, e = end, f = format) => {
    setBusy(true);
    setMsg("");
    try {
      const r = await fetch(`${API}/api/marketing/stats/export?start=${s}&end=${e}&format=${f}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Download failed");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `clientdeskva-launch-stats_${s}_to_${e}.${f}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
      setMsg("Downloaded.");
    } catch (err) {
      setMsg(err.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (preset.current) {
      download(preset.current.start, preset.current.end, "xlsx");
      window.history.replaceState(null, "", window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="bg-white rounded-2xl border border-[#E6E1D8] p-5 sm:p-6" data-testid="export-panel">
      <div className="flex items-center gap-2 mb-1">
        <Download size={18} className="text-[#E8712C]" />
        <h2 className="font-semibold text-[16px] text-[#1A2A3A]">Download spreadsheet</h2>
      </div>
      <p className="text-[13px] text-[#5B6B7B] mb-5">The Excel file has a Summary tab and a Daily tab. Type your ad spend in the orange column and cost per click fills in automatically.</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={lbl} htmlFor="ex-start">From</label>
          <input id="ex-start" type="date" className={field} value={start} max={end} onChange={(e) => setStart(e.target.value)} data-testid="export-start-date" />
        </div>
        <div>
          <label className={lbl} htmlFor="ex-end">To</label>
          <input id="ex-end" type="date" className={field} value={end} min={start} onChange={(e) => setEnd(e.target.value)} data-testid="export-end-date" />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-4">
        <div className="flex bg-[#F4F6F8] rounded-full p-1" role="radiogroup" aria-label="File format">
          {[["xlsx", "Excel"], ["csv", "CSV"]].map(([v, l]) => (
            <button key={v} type="button" role="radio" aria-checked={format === v} onClick={() => setFormat(v)} data-testid={`export-format-${v}`}
              className={`px-3.5 py-1.5 rounded-full text-[13px] font-semibold transition-colors ${format === v ? "bg-[#334E68] text-white" : "text-[#3D4F61]"}`}>
              {l}
            </button>
          ))}
        </div>
        <button type="button" onClick={() => download()} disabled={busy || !start || !end} data-testid="export-download-button"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#E8712C] hover:bg-[#C55A1F] disabled:opacity-50 text-white text-[14px] font-semibold transition-colors">
          <Download size={15} /> {busy ? "Preparing…" : "Download"}
        </button>
      </div>
      {msg && <p className="text-[13px] text-[#5B6B7B] mt-3" role="status" data-testid="export-message">{msg}</p>}
    </section>
  );
}

export function WeeklyEmailCard({ token }) {
  const [state, setState] = useState("");
  const send = async () => {
    setState("sending");
    try {
      const r = await fetch(`${API}/api/marketing/stats/weekly-email/send-now`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      setState(r.ok ? "sent" : "failed");
    } catch (_) {
      setState("failed");
    }
  };
  return (
    <section className="bg-white rounded-2xl border border-[#E6E1D8] p-5 sm:p-6" data-testid="weekly-email-card">
      <div className="flex items-center gap-2 mb-1">
        <Mail size={18} className="text-[#E8712C]" />
        <h2 className="font-semibold text-[16px] text-[#1A2A3A]">Weekly email</h2>
      </div>
      <p className="text-[13px] text-[#5B6B7B] mb-5">
        Every Monday at 8:00 AM Eastern, last week's totals, the change from the week before, the top campaigns and a
        spreadsheet download link go to <span className="font-semibold text-[#1A2A3A]">support@clientdeskva.com</span>.
      </p>
      <button type="button" onClick={send} disabled={state === "sending"} data-testid="weekly-email-send-now"
        className="px-5 py-2.5 rounded-full border border-[#334E68] text-[#334E68] hover:bg-[#334E68]/5 disabled:opacity-50 text-[14px] font-semibold transition-colors">
        {state === "sending" ? "Sending…" : "Send last week's summary now"}
      </button>
      {state === "sent" && <p className="text-[13px] text-[#2E7D32] mt-3" role="status" data-testid="weekly-email-status">Sent to support@clientdeskva.com.</p>}
      {state === "failed" && <p className="text-[13px] text-[#B42318] mt-3" role="alert" data-testid="weekly-email-status">Could not send the email. Please try again later.</p>}
    </section>
  );
}
